<template>
    <div class="admin-section structure-tab">
        <div class="structure-tab__toolbar">
            <StructureFilters
                v-model:search-query="filters.searchQuery"
                v-model:partner-id="filters.partnerId"
                :partners="structure.partners"
                @expand-all="expandAll"
                @collapse-all="expandedKeys.clear()"
            />
            <div class="structure-tab__create">
                <button
                    v-if="structure.abilities?.create_advertiser"
                    type="button"
                    class="btn-secondary"
                    @click="openDrawer(DRAWER_MODE.CREATE, ENTITY_KIND.ADVERTISER)"
                >
                    <span aria-hidden="true">+</span> Рекламодатель
                </button>
                <button
                    v-if="structure.abilities?.create_partner"
                    type="button"
                    class="btn-primary"
                    @click="openDrawer(DRAWER_MODE.CREATE, ENTITY_KIND.PARTNER)"
                >
                    <span aria-hidden="true">+</span> Партнёр
                </button>
            </div>
        </div>

        <StructureTree
            v-if="filtered.roots.length"
            :roots="filtered.roots"
            :query="filters.searchQuery"
            :expanded-keys="expandedKeys"
            :path-keys="filtered.pathKeys"
            :busy-key="busyKey"
            :agencies-by-partner="agenciesByPartner"
            :clients-by-advertiser="clientsByAdvertiser"
            @toggle="toggle"
            @action="handleAction"
            @open-users="partnerId => emit('open-users', partnerId)"
            @edit-markup="openMarkupDrawer"
        />

        <div v-else class="structure-tab__empty">
            <template v-if="!tree.length">
                <p class="structure-tab__empty-title">Структура пока пуста</p>
                <p class="structure-tab__empty-text">
                    {{ structure.abilities?.create_partner
                        ? 'Создайте партнёра — под ним появятся рекламодатели, бренды и кампании.'
                        : 'Вам ещё не выдан доступ ни к одному партнёру. Обратитесь к администратору.' }}
                </p>
            </template>
            <template v-else>
                <p class="structure-tab__empty-title">Ничего не нашлось</p>
                <p class="structure-tab__empty-text">Попробуйте изменить запрос или выбрать другого партнёра.</p>
                <button type="button" class="btn-secondary" @click="resetFilters">Сбросить фильтры</button>
            </template>
        </div>

        <StructureDrawer
            :open="drawer.open"
            :mode="drawer.mode"
            :kind="drawer.kind"
            :node="drawer.node"
            :parent="drawer.parent"
            :actor="actor"
            :partners="structure.partners"
            :advertisers="structure.advertisers"
            @close="drawer.open = false"
            @saved="onSaved"
        />

        <MarkupDrawer
            :open="markupDrawer.open"
            :target="markupDrawer.target"
            :entity="markupDrawer.entity"
            :partner="markupDrawer.partner"
            @close="markupDrawer.open = false"
            @saved="onMarkupSaved"
        />

        <ConfirmDialog
            :open="confirm.open"
            :title="confirmTitle"
            confirm-label="Удалить"
            tone="danger"
            :busy="confirm.busy"
            @confirm="runDelete"
            @cancel="confirm.open = false"
        >
            <template v-if="confirm.node">
                <p v-if="confirm.node.kind === ENTITY_KIND.PARTNER">
                    Партнёр <strong>{{ confirm.node.title }}</strong> и его наценки будут удалены.
                </p>
                <p v-else-if="confirm.node.kind === ENTITY_KIND.ADVERTISER">
                    Рекламодатель <strong>{{ confirm.node.title }}</strong> будет удалён, а клиенты потеряют выданный на него доступ.
                </p>
                <p v-else>Бренд <strong>{{ confirm.node.title }}</strong> будет удалён.</p>
                <p>Действие необратимо.</p>
            </template>
        </ConfirmDialog>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import axios from 'axios';
import StructureFilters from './structure/StructureFilters.vue';
import StructureTree from './structure/StructureTree.vue';
import StructureDrawer from './structure/StructureDrawer.vue';
import MarkupDrawer from './structure/MarkupDrawer.vue';
import ConfirmDialog from '../ui/ConfirmDialog.vue';
import {
    buildStructureTree,
    collectExpandableKeys,
    filterStructureTree,
    nodeKey,
} from './structure/buildStructureTree';
import {
    ENTITY_COLLECTION,
    ENTITY_ENDPOINT,
    ENTITY_KIND,
    ENTITY_LABEL,
    MARKUP_TARGET,
    STRUCTURE_ACTION,
} from '../../constants/structure';
import { ROLE, isClientRole } from '../../constants/roles';
import { DRAWER_MODE } from '../../constants/users';
import { useToast } from '../../composables/useToast';
import { extractErrorMessage } from '../../utils/httpErrors';

const props = defineProps({
    actor: { type: Object, required: true },
    users: { type: Array, required: true },
});

const structure = defineModel('structure', { type: Object, required: true });

const emit = defineEmits(['open-users']);

const toast = useToast();

// ===== Дерево и фильтры =====

const filters = reactive({ searchQuery: '', partnerId: '' });

const tree = computed(() => buildStructureTree(structure.value));
const filtered = computed(() => filterStructureTree(tree.value, {
    query: filters.searchQuery,
    partnerId: filters.partnerId,
}));

/** Единственного партнёра (типичный случай для агентства) показываем раскрытым сразу. */
const expandedKeys = reactive(new Set(structure.value.partners.length === 1
    ? [nodeKey(ENTITY_KIND.PARTNER, structure.value.partners[0].id)]
    : []));

const toggle = (key) => {
    if (expandedKeys.has(key)) expandedKeys.delete(key);
    else expandedKeys.add(key);
};

const expandAll = () => collectExpandableKeys(filtered.value.roots).forEach(key => expandedKeys.add(key));

const resetFilters = () => {
    filters.searchQuery = '';
    filters.partnerId = '';
};

// ===== Доступы пользователей (для чипов) =====

const groupUsersByScope = (isRelevant, scopeKey) => props.users
    .filter(user => !user.deleted_at && isRelevant(user.role?.code))
    .reduce((groups, user) => {
        (user.scope?.[scopeKey] || []).forEach((id) => {
            const key = Number(id);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(user);
        });
        return groups;
    }, new Map());

const agenciesByPartner = computed(() => groupUsersByScope(code => code === ROLE.AGENCY, 'partner_ids'));
const clientsByAdvertiser = computed(() => groupUsersByScope(isClientRole, 'advertiser_ids'));

// ===== Создание и редактирование =====

const drawer = reactive({
    open: false,
    mode: DRAWER_MODE.CREATE,
    kind: ENTITY_KIND.PARTNER,
    node: null,
    parent: {},
});

const openDrawer = (mode, kind, node = null, parent = {}) => {
    Object.assign(drawer, { open: true, mode, kind, node, parent });
};

const byName = (a, b) => a.name.localeCompare(b.name, 'ru');

const upsertEntity = (kind, entity) => {
    const collection = ENTITY_COLLECTION[kind];
    const others = structure.value[collection].filter(item => item.id !== entity.id);
    structure.value = { ...structure.value, [collection]: [...others, entity].sort(byName) };
};

const removeEntity = (kind, id) => {
    const collection = ENTITY_COLLECTION[kind];
    structure.value = { ...structure.value, [collection]: structure.value[collection].filter(item => item.id !== id) };
};

/** Бэкенд переносит кампании вслед за брендом (cascadeCampaignAdvertiserIds) — повторяем локально. */
const moveBrandCampaigns = (brand) => {
    structure.value = {
        ...structure.value,
        campaigns: structure.value.campaigns.map(campaign => (campaign.brand_id === brand.id
            ? { ...campaign, advertiser_id: brand.advertiser_id }
            : campaign)),
    };
};

/** Раскрыть путь до новой или перенесённой сущности, чтобы результат был виден сразу. */
const revealEntity = (kind, entity) => {
    if (kind === ENTITY_KIND.BRAND) {
        expandedKeys.add(nodeKey(ENTITY_KIND.ADVERTISER, entity.advertiser_id));
        const advertiser = structure.value.advertisers.find(item => item.id === entity.advertiser_id);
        if (advertiser?.partner_id) expandedKeys.add(nodeKey(ENTITY_KIND.PARTNER, advertiser.partner_id));
    }
    if (kind === ENTITY_KIND.ADVERTISER && entity.partner_id) {
        expandedKeys.add(nodeKey(ENTITY_KIND.PARTNER, entity.partner_id));
    }
};

const onSaved = ({ kind, mode, entity, message }) => {
    upsertEntity(kind, entity);
    if (kind === ENTITY_KIND.BRAND) moveBrandCampaigns(entity);
    if (mode === DRAWER_MODE.CREATE || kind !== ENTITY_KIND.PARTNER) revealEntity(kind, entity);

    drawer.open = false;
    toast.success(message);
};

// ===== Наценки =====

const markupDrawer = reactive({
    open: false,
    target: MARKUP_TARGET.PARTNER,
    entity: null,
    partner: null,
});

const openMarkupDrawer = ({ target, entity, partner }) => {
    Object.assign(markupDrawer, { open: true, target, entity, partner });
};

const onMarkupSaved = ({ target, entity, markup }) => {
    if (target === MARKUP_TARGET.PARTNER) {
        upsertEntity(ENTITY_KIND.PARTNER, { ...entity, markup });
    } else {
        structure.value = {
            ...structure.value,
            campaigns: structure.value.campaigns.map(campaign => (campaign.key === entity.key ? { ...campaign, markup } : campaign)),
        };
    }

    markupDrawer.open = false;
    toast.success(target === MARKUP_TARGET.PARTNER
        ? `Наценки партнёра «${entity.name}» сохранены`
        : `Наценка кампании «${entity.name}» сохранена`);
};

// ===== Удаление =====

const confirm = reactive({ open: false, node: null, busy: false });
const busyKey = ref(null);

const confirmTitle = computed(() => (confirm.node
    ? `Удалить ${ENTITY_LABEL[confirm.node.kind].accusative}?`
    : ''));

const runDelete = async () => {
    const { node } = confirm;
    confirm.busy = true;
    busyKey.value = node.key;

    try {
        await axios.delete(`${ENTITY_ENDPOINT[node.kind]}/${node.entity.id}`);
        removeEntity(node.kind, node.entity.id);
        expandedKeys.delete(node.key);
        toast.success(`${ENTITY_LABEL[node.kind].one} «${node.title}» удалён`);
        confirm.open = false;
    } catch (error) {
        toast.error(extractErrorMessage(error));
    } finally {
        confirm.busy = false;
        busyKey.value = null;
    }
};

const handleAction = (action, node) => {
    if (action === STRUCTURE_ACTION.CREATE_ADVERTISER) {
        openDrawer(DRAWER_MODE.CREATE, ENTITY_KIND.ADVERTISER, null, { partnerId: node.entity.id });
    } else if (action === STRUCTURE_ACTION.CREATE_BRAND) {
        openDrawer(DRAWER_MODE.CREATE, ENTITY_KIND.BRAND, null, { advertiserId: node.entity.id });
    } else if (action === STRUCTURE_ACTION.EDIT) {
        openDrawer(DRAWER_MODE.EDIT, node.kind, node);
    } else if (action === STRUCTURE_ACTION.EDIT_MARKUP) {
        openMarkupDrawer({ target: MARKUP_TARGET.PARTNER, entity: node.entity, partner: node.entity });
    } else if (action === STRUCTURE_ACTION.DELETE) {
        Object.assign(confirm, { open: true, node, busy: false });
    }
};
</script>

<style scoped lang="scss">
@use '../../../scss/pages/admin/admin-tokens' as *;

.structure-tab__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-lg;
    margin-bottom: $spacing-xl;
}

.structure-tab__create {
    display: flex;
    gap: $spacing-sm;
    flex: 0 0 auto;

    button { white-space: nowrap; }
}

.structure-tab__empty {
    padding: $spacing-2xl;
    border: 1px dashed $admin-border-hover;
    border-radius: $radius-lg;
    text-align: center;
    background: $admin-bg-primary;
}

.structure-tab__empty-title {
    margin: 0 0 $spacing-xs;
    font-size: $font-size-lg;
    font-weight: 600;
    color: $admin-text-primary;
}

.structure-tab__empty-text {
    margin: 0 0 $spacing-lg;
    color: $admin-text-muted;
}
</style>
